import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { I18nProvider, useI18n } from './context/I18nContext';
import { LoginProvider, useLogin } from './context/LoginContext';
import { MatrixClientProvider } from './context/MatrixClientProvider';
import { useMatrixClient } from './context/MatrixContext';
import { LayoutProvider, useLayout } from './context/LayoutContext';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import { ModalProvider } from './context/ModalContext';
import { RecoveryProvider } from './context/RecoveryContext/index';
import { SettingsProvider } from './context/SettingsContext';
import { NotificationProvider } from './context/NotificationContext';
import { SettingsModalProvider, useSettingsModal } from './context/SettingsModalContext';
import { DownloadProvider } from './context/DownloadContext';
import { ToastProvider } from './context/ToastContext';
import { AvatarProvider } from './context/AvatarContext';
import { DisambiguationProvider } from './context/DisambiguationContext';
import { LoadingProvider } from './context/LoadingContext';
import { ForwardingProvider, useForwarding } from './context/ForwardingContext';
import ForwardModal from './components/Modal/ForwardModal';
import ChatExpiryReminderModal from './components/Modal/ChatExpiryReminderModal';
import { useChatExpiryReminder } from './hooks/useChatExpiryReminder';
import LoadingOverlay from './components/Shared/LoadingOverlay';
import IndexedDBGuard from './components/IndexedDBGuard';
import CryptoIntegrityGuard from './components/CryptoIntegrityGuard';
import ToastContainer from './components/Common/ToastContainer';
import NetworkStatusBanner from './components/Common/NetworkStatusBanner';
import VerificationCoordinator from './components/verification/VerificationCoordinator';
import AvatarSync from './components/AvatarSync';
import NotificationHandler from './components/NotificationHandler';
import NotificationPermissionBanner from './components/NotificationPermissionBanner';
import useDownloadNavigationGuard from './hooks/useDownloadNavigationGuard';
import useAutoLogout from './hooks/useAutoLogout';
import useInviteFiltering from './hooks/useInviteFiltering';
import useServerNoticesAutoJoin from './hooks/useServerNoticesAutoJoin';
import { useWindowWidth } from './hooks/useWindowWidth';
import { useRoomList } from './hooks/useRoomList';
import { useChannels } from './hooks/useChannels';
import Menu from './components/Menu/Menu';
import ConversationsPage from './components/Page/Conversations/ConversationsPage';
import ChannelsPage from './components/Page/ChannelsPage';
import ContactsPage from './components/Page/Contacts/ContactsPage';
import LoginPage from './components/Login/LoginPage';
import SSOCallbackHandler from './components/SSO/SSOCallbackHandler';
import Modal from './components/Modal/Modal';
import SettingsModal from './components/SettingsModal';
import { RecoveryFlow } from './components/Recovery';
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary/ChunkErrorBoundary';
import { UpdateNotification } from './components/UpdateNotification/UpdateNotification';
import SecurityUpdateController from './components/SecurityUpdate/SecurityUpdateController';
import { VersionCheckProvider } from './context/VersionCheckContext';
import logger from './utils/logger';
import './App.css';

// Wrapper component that provides client to RecoveryProvider
const RecoveryProviderWithClient = ({ children }) => {
  const client = useMatrixClient();
  return (
    <RecoveryProvider client={client}>
      {children}
    </RecoveryProvider>
  );
};

RecoveryProviderWithClient.propTypes = {
  children: PropTypes.node.isRequired,
};

// Wrapper component that conditionally renders ForwardModal
const ForwardModalWrapper = () => {
  const { showForwardModal, closeForwardModal } = useForwarding();
  logger.debug('[DEBUG_FORWARD] ForwardModalWrapper render', { showForwardModal });
  if (showForwardModal) {
    logger.debug('[DEBUG_FORWARD] ForwardModalWrapper: Rendering ForwardModal');
  }
  return showForwardModal ? <ForwardModal onClose={closeForwardModal} /> : null;
};

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useLogin();
  const { t } = useI18n();

  if (isLoading) {
    return <LoadingOverlay message={t('ScreenOverlay.loading')} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// Main application layout with outlet for nested routes
const AppLayout = () => {
  const { isOpen: isSettingsOpen, initialPage: settingsInitialPage, openSettings, closeSettings } = useSettingsModal();
  const client = useMatrixClient();

  // Track window width to auto-hide menu and sidebars below 700px
  const isWideEnoughForSidebars = useWindowWidth(700);

  // Initialize the auto-logout timer
  useAutoLogout();

  // Audit rooms approaching server deletion threshold (default 180 days)
  const { expiringRooms, showModal: showExpiryModal, dismiss: dismissExpiry, removeRoom } = useChatExpiryReminder();

  // Activate download navigation guard to prevent accidental cancellation
  useDownloadNavigationGuard();

  // Activate invite filtering based on Incoming Invites settings
  useInviteFiltering();

  // Auto-accept server notices room invites
  useServerNoticesAutoJoin();

  // Get room lists for unread badge aggregation
  const { roomList: conversations } = useRoomList();
  const { channelList: channels } = useChannels();

  // Calculate if there are any unread items or pending invites
  // Using .some() for efficiency - we only need boolean, not total count
  const chatsHasUnread = conversations.some(conv => (conv.unreadCount || 0) > 0 || conv.isInvite);
  const groupsHasUnread = channels.some(ch => (ch.unreadCount || 0) > 0 || ch.isInvite);

  // Add avatar cache debug helper in development mode
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const indexedDBService = require('./services/indexedDB').default;
      const { avatarManager } = require('./utils/avatarManager');

      window.avatarCacheDebug = {
        stats: () => {
          const managerStats = avatarManager.getStats();
          console.log('Avatar Manager Stats:', managerStats);
          return managerStats;
        },
        indexedDbStats: async () => {
          const dbStats = await indexedDBService.getAvatarCacheStats();
          console.log('IndexedDB Cache Stats:', dbStats);
          return dbStats;
        },
        clear: () => {
          avatarManager.clearAll();
          console.log('All avatars cleared (memory + IndexedDB)');
        },
        invalidate: async (userId) => {
          await avatarManager.invalidate(userId);
          console.log(`Avatar invalidated for ${userId}`);
        },
        cache: () => {
          return Array.from(avatarManager.cache.entries()).map(([userId, data]) => ({
            userId,
            refCount: data.refCount,
            age: Date.now() - data.timestamp,
            url: data.blobUrl,
          }));
        },
      };

      // console.log('🔍 Avatar Manager debug helper available via window.avatarCacheDebug');
      // console.log('  - window.avatarCacheDebug.stats()              // Get memory cache statistics');
      // console.log('  - await window.avatarCacheDebug.indexedDbStats() // Get IndexedDB statistics');
      // console.log('  - window.avatarCacheDebug.cache()              // View all cached avatars');
      // console.log('  - window.avatarCacheDebug.clear()              // Clear all avatars');
      // console.log('  - await window.avatarCacheDebug.invalidate(userId) // Invalidate specific avatar');
    }

    return () => {
      if (process.env.NODE_ENV === 'development') {
        delete window.avatarCacheDebug;
      }
    };
  }, [client]);

  // Note: Session invalidation is handled by LoginContext (clears auth state)
  // and LoginPage (shows modal). No listener needed here - LoginContext's listener
  // is registered earlier in the component tree and catches the event.

  const {
    navigateToConversation,
    navigateToContact,
    navigateToChannel,
    currentPage,
  } = useNavigation();

  const {
    isMenuExpanded,
    maxMenuWidth,
    minMenuWidth,
  } = useLayout();

  const renderMenu = () => {
    // Hide menu when screen width < 700px
    if (!isWideEnoughForSidebars) return null;

    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: maxMenuWidth,
    };

    return (
      <div style={style}>
        <Menu
          currentPage={currentPage}
          onSettingsClick={() => openSettings()}
          setToConversations={() => navigateToConversation()}
          setToContacts={() => navigateToContact()}
          setToChannels={() => navigateToChannel()}
          chatsHasUnread={chatsHasUnread}
          groupsHasUnread={groupsHasUnread}
        />
      </div>
    );
  };

  const pageStyle = {
    display: 'flex',
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    // Full width when menu is hidden by responsive constraint
    width: isWideEnoughForSidebars
      ? `calc(100% - ${(isMenuExpanded) ? maxMenuWidth : minMenuWidth}px)`
      : '100%',
  };

  return (
    <div className="app-container">
      {renderMenu()}
      <div className="app-page" style={pageStyle}>
        <Outlet />
      </div>
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={closeSettings}
        initialPage={settingsInitialPage}
      />
      {showExpiryModal && (
        <ChatExpiryReminderModal
          rooms={expiringRooms}
          onClose={dismissExpiry}
          onRoomLeft={removeRoom}
        />
      )}
    </div>
  );
};

const AppRouter = () => {
  return (
    <Router>
      <NavigationProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/conversations" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <ProtectedRoute>
                <MatrixClientProvider>
                  <AvatarProvider>
                    <DisambiguationProvider>
                      <AvatarSync />
                      <NotificationHandler />
                      <NotificationPermissionBanner />
                      <DownloadProvider>
                        <ForwardingProvider>
                          <SettingsModalProvider>
                            <RecoveryProviderWithClient>
                              <AppLayout />
                              <RecoveryFlow />
                              <VerificationCoordinator />
                            </RecoveryProviderWithClient>
                          </SettingsModalProvider>
                          <ForwardModalWrapper />
                        </ForwardingProvider>
                      </DownloadProvider>
                    </DisambiguationProvider>
                  </AvatarProvider>
                </MatrixClientProvider>
              </ProtectedRoute>
            }
          >
            <Route path="/conversations" element={<ConversationsPage />} />
            <Route path="/conversations/new" element={<ConversationsPage />} />
            <Route path="/conversations/:id" element={<ConversationsPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/contacts/:id" element={<ContactsPage />} />
            <Route path="/channels" element={<ChannelsPage />} />
            <Route path="/channels/new" element={<ChannelsPage />} />
            <Route path="/channels/:id" element={<ChannelsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/conversations" replace />} />
        </Routes>
      </NavigationProvider>
    </Router>
  );
};

// Wrapper to provide contexts
const AppWithRecovery = () => {
  // RecoveryProvider needs to be inside a component that can access the Matrix client
  // So we'll restructure to have RecoveryProvider inside the authenticated area
  return (
    <LayoutProvider>
      <LoadingProvider>
        <ModalProvider>
          <ToastProvider>
            <VersionCheckProvider>
              <SecurityUpdateController />
              <UpdateNotification />
              <NetworkStatusBanner />
              <AppRouter />
              <Modal />
              <ToastContainer />
            </VersionCheckProvider>
          </ToastProvider>
        </ModalProvider>
      </LoadingProvider>
    </LayoutProvider>
  );
};

const App = () => (
  <ChunkErrorBoundary>
    <CryptoIntegrityGuard>
    <IndexedDBGuard>
      <I18nProvider defaultLang="en">
        <SettingsProvider>
          <NotificationProvider>
            <LoginProvider>
              <SSOCallbackHandler />
              <AppWithRecovery />
            </LoginProvider>
          </NotificationProvider>
        </SettingsProvider>
      </I18nProvider>
    </IndexedDBGuard>
    </CryptoIntegrityGuard>
  </ChunkErrorBoundary>
);

export default App;
